import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError} from "../../api/client";
import { apiGet, apiPost } from "../../api/client";

interface CoaTarget { kind?: string; type?: string; recipientRole?: string; archetype?: string; [k: string]: unknown }

interface Coa {
  coa: "attach" | "new_play" | "new_card" | "signal";
  target?: CoaTarget;
  confidence: number;
  rationale: string;
  estimatedImpact?: "low" | "medium" | "high";
}

interface Spot {
  id: string;
  workspaceId: string;
  submittedByUserId: string;
  kind: string;
  title: string;
  description: string;
  state: "assessing" | "awaiting_approval" | "actioned" | "dismissed" | "expired";
  currentDisposition: string | null;
  approverUserId: string | null;
  approverRole: string | null;
  clientTag: string | null;
  impactEstimate: string | null;
  attachments: Array<{ url: string; filename?: string; mime?: string }>;
  assessment: { coas: Coa[]; assessedAt: string; modelVersion?: string } | null;
  assessmentAt: string | null;
  createdAt: string;
  actionedAt: string | null;
}

interface SpotAction {
  id: string;
  spotId: string;
  coa: string;
  targetKind: string | null;
  targetId: string | null;
  rationale: string;
  confidence: number | null;
  approverNote: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  autoApproved: boolean;
  outcome: string | null;
  outcomeAt: string | null;
  createdAt: string;
}

interface DetailResponse {
  spot: Spot;
  actions: SpotAction[];
}

const COA_LABEL: Record<string, string> = {
  attach: "Attach to existing play",
  new_play: "Trigger new play",
  new_card: "Route decision card",
  signal: "File as signal",
  dismiss: "Dismiss",
};

const COA_TONE: Record<string, "approve" | "return" | "note"> = {
  attach: "approve",
  new_play: "approve",
  new_card: "approve",
  signal: "note",
  dismiss: "return",
};

export function SpotDetail() {
  const { workspaceId, spotId } = useParams<{ workspaceId: string; spotId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [approverNote, setApproverNote] = useState("");
  const [pendingIdx, setPendingIdx] = useState<number | null>(null);
  const [assessingTimedOut, setAssessingTimedOut] = useState(false);
  // Tracks the wall-clock timestamp when we first observed 'assessing' state
  // so we can cap polling at 60 seconds and surface a warning instead of
  // polling indefinitely.
  const assessingFirstSeenAt = useRef<number | null>(null);
  const ASSESSING_TIMEOUT_MS = 60_000;

  const q = useQuery<DetailResponse, ApiError>({
    queryKey: ["spot", workspaceId, spotId],
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/spots/${spotId}`),
    enabled: !!workspaceId && !!spotId,
    // Phase A is synchronous, but a polling fallback handles the race where
    // the spot was just created and the assessment hasn't landed yet.
    // Stop polling after 60 s to avoid infinite background requests.
    refetchInterval: (q) => {
      if (q.state.data?.spot.state !== "assessing") {
        assessingFirstSeenAt.current = null;
        return false;
      }
      if (assessingTimedOut) return false;
      const now = Date.now();
      if (assessingFirstSeenAt.current === null) {
        assessingFirstSeenAt.current = now;
      }
      if (now - assessingFirstSeenAt.current >= ASSESSING_TIMEOUT_MS) {
        setAssessingTimedOut(true);
        return false;
      }
      return 1500;
    },
    // Retry transient failures (5xx, network) up to 2 times. Skip retries
    // on the deterministic client-error statuses (4xx) — those won't
    // change on retry and would just delay the actionable error UI.
    retry: (failureCount, err) => {
      const status = (err as ApiError | undefined)?.status ?? 0;
      if (status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });

  const approveM = useMutation<unknown, ApiError, { coaIndex: number }>({
    mutationFn: ({ coaIndex }) =>
      apiPost(`/api/workspaces/${workspaceId}/spots/${spotId}/action`, {
        coa_index: coaIndex,
        ...(approverNote.trim() && { approver_note: approverNote.trim() }),
      }),
    onSuccess: async () => {
      setApproverNote("");
      await qc.invalidateQueries({ queryKey: ["spot", workspaceId, spotId] });
      await qc.invalidateQueries({ queryKey: ["spots", workspaceId] });
    },
    onSettled: () => setPendingIdx(null),
  });

  const dismissM = useMutation<unknown, ApiError, void>({
    mutationFn: () =>
      apiPost(`/api/workspaces/${workspaceId}/spots/${spotId}/dismiss`, {
        ...(approverNote.trim() && { approver_note: approverNote.trim() }),
      }),
    onSuccess: async () => {
      setApproverNote("");
      await qc.invalidateQueries({ queryKey: ["spot", workspaceId, spotId] });
      await qc.invalidateQueries({ queryKey: ["spots", workspaceId] });
    },
  });

  if (q.isLoading) return <div className="review-page">Loading spot…</div>;
  if (q.error || !q.data) {
    // Distinguish the three real failure modes so the user knows what
    // to do. 404 in this surface almost always means the spot exists
    // but not in the workspace currently in the URL (stale list, or
    // workspace switch between list and click). 403 is the
    // non-triage-role gate. Anything else is transient — show retry.
    const status = q.error?.status ?? 0;
    const isNotFound = status === 404;
    const isForbidden = status === 403;
    return (
      <div className="review-page">
        <button type="button" className="review-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="review-banner error" data-testid="spot-detail-error">
          {isNotFound ? (
            <>
              <strong>This spot isn’t in the current workspace.</strong>{" "}
              It may belong to a different workspace you’re a member of.
              Open{" "}
              <a
                onClick={() => navigate("/me/spotted")}
                style={{ cursor: "pointer", textDecoration: "underline" }}
              >
                My Spotted
              </a>{" "}
              to find it across all your workspaces.
            </>
          ) : isForbidden ? (
            <>
              <strong>You don’t have access to this spot.</strong>{" "}
              Only the reporter and triage roles (CxO / operator /
              approver) can open spots they didn’t file.
            </>
          ) : (
            <>
              <strong>Couldn’t load this spot.</strong>{" "}
              {q.error?.message ?? "Network or server error."}
            </>
          )}
        </div>
        {!isNotFound && !isForbidden && (
          <button
            type="button"
            className="btn"
            style={{ marginTop: 12 }}
            onClick={() => q.refetch()}
            disabled={q.isFetching}
            data-testid="spot-detail-retry"
          >
            {q.isFetching ? "Retrying…" : "Retry"}
          </button>
        )}
      </div>
    );
  }

  const { spot, actions } = q.data;
  const coas = spot.assessment?.coas ?? [];
  const open = spot.state === "awaiting_approval";

  return (
    <div className="review-page">
      <button
        type="button"
        className="review-back"
        onClick={() => navigate(`/workspaces/${workspaceId}/spotted`)}
      >
        ← Spotted
      </button>

      <p className="review-eyebrow">{spot.kind.replace(/_/g, " ")}</p>
      <h1 className="review-title">{spot.title}</h1>

      <div className="review-meta">
        <div>{spot.description}</div>
        <div style={{ marginTop: 10, color: "var(--text-3, #64748b)", fontSize: 12 }}>
          {spot.clientTag ? <>Client: <strong>{spot.clientTag}</strong> · </> : null}
          {spot.impactEstimate ? <>Impact: <strong>{spot.impactEstimate}</strong> · </> : null}
          State: <strong>{spot.state.replace(/_/g, " ")}</strong>
          {spot.currentDisposition ? <> · Disposition: <strong>{spot.currentDisposition}</strong></> : null}
        </div>
      </div>

      {spot.attachments && spot.attachments.length > 0 && (
        <div className="spot-detail-gallery">
          {spot.attachments.map((a, i) =>
            (a.mime ?? "").startsWith("image/") || /\.(png|jpg|jpeg|gif|webp|heic|heif)$/i.test(a.filename ?? a.url) ? (
              <a key={i} href={a.url} target="_blank" rel="noopener noreferrer">
                <img src={a.url} alt={a.filename ?? `attachment ${i + 1}`} />
              </a>
            ) : (
              <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" style={{ padding: 12, width: "auto", height: "auto" }}>
                📎 {a.filename ?? "file"}
              </a>
            ),
          )}
        </div>
      )}

      {spot.state === "assessing" && !assessingTimedOut && (
        <div className="review-banner ok" style={{ marginTop: 16 }}>
          ⏳ XAMI is assessing this spot…
        </div>
      )}
      {spot.state === "assessing" && assessingTimedOut && (
        <div className="review-banner error" style={{ marginTop: 16 }} data-testid="spot-assessing-timeout">
          Assessment is taking longer than expected. Please refresh the page or check back later.
        </div>
      )}

      {coas.length > 0 && (
        <section className="review-section">
          <h2 className="review-section-title">XAMI · proposed courses of action</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {coas.map((c, i) => (
              <div key={i} className="coa-card">
                <div className="coa-card-head">
                  <span className={`pill coa-pill coa-${c.coa}`}>{COA_LABEL[c.coa]}</span>
                  <span className="coa-confidence">
                    confidence {Math.round(c.confidence * 100)}%
                  </span>
                  {c.estimatedImpact && (
                    <span className="coa-impact">est. {c.estimatedImpact} impact</span>
                  )}
                </div>
                <p className="coa-rationale">{c.rationale}</p>
                {open && (
                  <div className="review-actions" style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className={`review-btn ${COA_TONE[c.coa]}`}
                      disabled={approveM.isPending || dismissM.isPending}
                      onClick={() => {
                        setPendingIdx(i);
                        approveM.mutate({ coaIndex: i });
                      }}
                    >
                      {pendingIdx === i && approveM.isPending ? "Approving…" : "Approve"}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {open && (
            <>
              <label className="review-field" style={{ marginTop: 16 }}>
                <span className="review-field-label">
                  <strong>Approver note</strong>
                  <span className="review-field-hint">attached to whichever COA you take</span>
                </span>
                <textarea
                  rows={3}
                  className="review-textarea"
                  placeholder="Why you chose this action (optional)…"
                  value={approverNote}
                  onChange={(e) => setApproverNote(e.target.value)}
                />
              </label>
              <div className="review-actions" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="review-btn return"
                  disabled={dismissM.isPending}
                  onClick={() => dismissM.mutate()}
                >
                  {dismissM.isPending ? "Dismissing…" : "Dismiss"}
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {actions.length > 0 && (
        <section className="review-section">
          <h2 className="review-section-title">Action history</h2>
          <ul className="review-comment-list">
            {actions.map((a) => (
              <li key={a.id} className="review-comment">
                <div className="review-comment-head">
                  <span className="author">{COA_LABEL[a.coa] ?? a.coa}</span>
                  {a.autoApproved ? (
                    <span className="review-comment-decision approve">auto</span>
                  ) : (
                    <span className="review-comment-decision approve">approved</span>
                  )}
                  <span>
                    · {a.approvedAt
                        ? new Date(a.approvedAt).toLocaleString(undefined, {
                            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                          })
                        : "pending"}
                  </span>
                </div>
                <p className="review-comment-body">{a.rationale}</p>
                {a.approverNote && (
                  <div className="review-anchor-chip">{a.approverNote}</div>
                )}
                {a.outcome && (
                  <div style={{ marginTop: 6, fontSize: 13, color: "var(--text-2, #475569)" }}>
                    <strong>Outcome:</strong> {a.outcome}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
